import { createSignal } from "solid-js";
import type { AuthMeResponse, TenantRole } from "../api/types";

/**
 * The authenticated user, as published by AppShell — by its auth-gate
 * `onMount` and again by the throttled refresh a socket reconnect schedules
 * (FLOW-984, see `authRefreshThrottle`).
 *
 * AppShell renders page content as `props.children`, so it has no prop path
 * to hand the org-level `tenant_role` down to routed pages (ProjectsPage,
 * ProjectSettingsPage, WelcomeWizard's siblings, ...) that need it to decide
 * what to render. Rather than have each of those issue its own `getMe()`
 * call, AppShell publishes the response it already fetched here, and pages
 * read it back through `tenantRole()`.
 *
 * This store does NOT fetch on its own, and it does not guarantee a value by
 * the time a page first renders: AppShell's auth gate is an overlay, so
 * `props.children` mounts while a cold load's `getMe()` is still in flight.
 * Read it through the accessors inside a tracked scope and the page re-renders
 * when the user lands; do not copy it into a local signal, which would miss
 * both that first publish and every later refresh.
 */
const [currentUser, setCurrentUser] = createSignal<AuthMeResponse | null>(null);

const tenantRole = (): TenantRole | null => currentUser()?.tenant_role ?? null;

export { currentUser, setCurrentUser, tenantRole };
